// COMPLETE FIXED VERSION - Replace your entire MeetingRoom.js file with this

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Button, Row, Col, Alert, Card, Badge, 
  Dropdown, ProgressBar, Toast, ToastContainer
} from 'react-bootstrap';
import { 
  BsMic, BsMicMute, BsCameraVideo, BsCameraVideoOff, 
  BsTelephoneX, BsTelephone, BsThreeDots, BsShare,
  BsChatDots, BsPeople, BsFullscreen, BsGear, BsRecordCircle,
  BsWifi, BsWifiOff, BsDisplay, BsDisplayFill,
  BsHandThumbsUp, BsHandThumbsDown, BsVolumeUp, BsVolumeMute
} from 'react-icons/bs';
import { connect } from 'twilio-video';
import authStorage from '../../utilities/Storage';
import './MeetingRoom.css';

function MeetingRoom() {
  const { roomName } = useParams();
  const navigate = useNavigate();
  const [room, setRoom] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [identity, setIdentity] = useState('');
  const [error, setError] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [connectionQuality, setConnectionQuality] = useState('excellent');
  const [recordingTime, setRecordingTime] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showSelfView, setShowSelfView] = useState(true);
  
  const localVideoRef = useRef();
  const localVideoContainerRef = useRef(); // NEW: Container for local video
  const remoteMediaDivRef = useRef();
  const recordingIntervalRef = useRef();

  useEffect(() => {
    const storedUser = authStorage.getUser();
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        setUserData(user);
        setIdentity(user.full_name || user.email.split('@')[0]);
      } catch (e) {
        console.error("Failed to parse user data:", e);
      }
    }

    return () => {
      handleLeave();
    };
  }, []);

  useEffect(() => {
    if (identity && userData) {
      handleAutoJoin();
    }
  }, [identity, userData]);

  useEffect(() => {
    if (isRecording) {
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(recordingIntervalRef.current);
    }
    return () => clearInterval(recordingIntervalRef.current);
  }, [isRecording]);

  const handleAutoJoin = async () => {
    if (!identity.trim()) return;

    setIsConnecting(true);
    setError('');

    try {
      const response = await fetch('https://backend-livestream-production.up.railway.app/api/generate-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomName, identity }),
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Could not retrieve token');

      const newRoom = await connect(result.token, {
        name: roomName,
        audio: true,
        video: { width: 1280, height: 720 }
      });

      setRoom(newRoom);
      
      // FIXED: Setup local video track properly
      setupLocalTrack(newRoom.localParticipant);

      newRoom.participants.forEach(handleParticipantConnected);
      newRoom.on('participantConnected', handleParticipantConnected);
      newRoom.on('participantDisconnected', handleParticipantDisconnected);

      setParticipants(Array.from(newRoom.participants.values()));
    } catch (err) {
      setError(err.message);
    } finally {
      setIsConnecting(false);
    }
  };

  // FIXED: Better local track setup
  const setupLocalTrack = (participant) => {
    console.log('Setting up local tracks...');
    
    // Attach video tracks
    participant.videoTracks.forEach(publication => {
      console.log('Video track found:', publication);
      if (publication.track && localVideoRef.current) {
        const videoElement = publication.track.attach();
        videoElement.style.width = '100%';
        videoElement.style.height = '100%';
        videoElement.style.objectFit = 'cover';
        videoElement.style.transform = 'scaleX(-1)'; // Mirror effect
        
        // Clear previous content and attach
        if (localVideoContainerRef.current) {
          localVideoContainerRef.current.innerHTML = '';
          localVideoContainerRef.current.appendChild(videoElement);
        }
      }
    });

    // Also handle when tracks are published (in case they come later)
    participant.on('trackPublished', publication => {
      console.log('Track published:', publication);
      if (publication.kind === 'video' && publication.track) {
        const videoElement = publication.track.attach();
        videoElement.style.width = '100%';
        videoElement.style.height = '100%';
        videoElement.style.objectFit = 'cover';
        videoElement.style.transform = 'scaleX(-1)';
        
        if (localVideoContainerRef.current) {
          localVideoContainerRef.current.innerHTML = '';
          localVideoContainerRef.current.appendChild(videoElement);
        }
      }
    });
  };

  const handleParticipantConnected = (participant) => {
    console.log('Participant connected:', participant.identity);
    setParticipants(prev => [...prev, participant]);

    participant.on('trackSubscribed', (track) => {
      console.log('Track subscribed:', track.kind);
      
      if (track.kind === 'video') {
        const videoElement = track.attach();
        videoElement.style.width = '100%';
        videoElement.style.height = '100%';
        videoElement.style.objectFit = 'cover';
        videoElement.style.borderRadius = '12px';
        
        // Create container for remote video
        const container = document.createElement('div');
        container.className = 'remote-video-tile';
        container.style.position = 'relative';
        container.style.minWidth = '300px';
        container.style.minHeight = '225px';
        container.style.backgroundColor = '#1a1a1a';
        container.style.borderRadius = '12px';
        container.style.overflow = 'hidden';
        container.setAttribute('data-participant-sid', participant.sid);
        container.setAttribute('data-track-sid', track.sid);
        
        // Create label overlay
        const overlay = document.createElement('div');
        overlay.style.position = 'absolute';
        overlay.style.bottom = '12px';
        overlay.style.left = '12px';
        overlay.style.padding = '4px 12px';
        overlay.style.backgroundColor = 'rgba(0,0,0,0.7)';
        overlay.style.color = 'white';
        overlay.style.borderRadius = '6px';
        overlay.style.fontSize = '14px';
        overlay.textContent = participant.identity;
        
        container.appendChild(videoElement);
        container.appendChild(overlay);
        remoteMediaDivRef.current?.appendChild(container);
      } else if (track.kind === 'audio') {
        const audioElement = track.attach();
        remoteMediaDivRef.current?.appendChild(audioElement);
      }
    });

    participant.on('trackUnsubscribed', (track) => {
      console.log('Track unsubscribed:', track.kind);
      track.detach().forEach(el => el.remove());
      
      // Remove empty containers
      const containers = remoteMediaDivRef.current?.querySelectorAll(`[data-track-sid="${track.sid}"]`);
      containers?.forEach(container => {
        if (container.children.length === 1) { // Only overlay left
          container.remove();
        }
      });
    });

    // Handle already subscribed tracks
    participant.tracks.forEach(publication => {
      if (publication.isSubscribed && publication.track) {
        const track = publication.track;
        
        if (track.kind === 'video') {
          const videoElement = track.attach();
          videoElement.style.width = '100%';
          videoElement.style.height = '100%';
          videoElement.style.objectFit = 'cover';
          videoElement.style.borderRadius = '12px';
          
          const container = document.createElement('div');
          container.className = 'remote-video-tile';
          container.style.position = 'relative';
          container.style.minWidth = '300px';
          container.style.minHeight = '225px';
          container.style.backgroundColor = '#1a1a1a';
          container.style.borderRadius = '12px';
          container.style.overflow = 'hidden';
          container.setAttribute('data-participant-sid', participant.sid);
          container.setAttribute('data-track-sid', track.sid);
          
          const overlay = document.createElement('div');
          overlay.style.position = 'absolute';
          overlay.style.bottom = '12px';
          overlay.style.left = '12px';
          overlay.style.padding = '4px 12px';
          overlay.style.backgroundColor = 'rgba(0,0,0,0.7)';
          overlay.style.color = 'white';
          overlay.style.borderRadius = '6px';
          overlay.style.fontSize = '14px';
          overlay.textContent = participant.identity;
          
          container.appendChild(videoElement);
          container.appendChild(overlay);
          remoteMediaDivRef.current?.appendChild(container);
        } else if (track.kind === 'audio') {
          const audioElement = track.attach();
          remoteMediaDivRef.current?.appendChild(audioElement);
        }
      }
    });
  };

  const handleParticipantDisconnected = (participant) => {
    console.log('Participant disconnected:', participant.identity);
    setParticipants(prev => prev.filter(p => p.sid !== participant.sid));
    
    // Remove all elements for this participant
    const elements = remoteMediaDivRef.current?.querySelectorAll(`[data-participant-sid="${participant.sid}"]`);
    elements?.forEach(el => el.remove());
  };

  const detachTrack = (track) => {
    track.detach().forEach(el => el.remove());
  };

  const handleLeave = () => {
    if (room) {
      room.disconnect();
      setRoom(null);
      setParticipants([]);
    }

    // Clean up local video
    if (localVideoContainerRef.current) {
      localVideoContainerRef.current.innerHTML = '';
    }

    // Clean up remote videos
    if (remoteMediaDivRef.current) {
      remoteMediaDivRef.current.innerHTML = '';
    }
    
    navigate('/chat-community');
  };

  const toggleMute = () => {
    if (room) {
      room.localParticipant.audioTracks.forEach(publication => {
        if (publication.track.isEnabled) {
          publication.track.disable();
        } else {
          publication.track.enable();
        }
      });
      setIsMuted(!isMuted);
    }
  };

  const toggleVideo = () => {
    if (room) {
      room.localParticipant.videoTracks.forEach(publication => {
        if (publication.track.isEnabled) {
          publication.track.disable();
        } else {
          publication.track.enable();
        }
      });
      setIsVideoOff(!isVideoOff);
    }
  };

  const toggleScreenShare = () => {
    setIsScreenSharing(!isScreenSharing);
    // Implement screen sharing logic here
  };

  const toggleRecording = () => {
    setIsRecording(!isRecording);
    setRecordingTime(0);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getConnectionQualityColor = () => {
    switch (connectionQuality) {
      case 'excellent': return 'success';
      case 'good': return 'info';
      case 'poor': return 'warning';
      case 'bad': return 'danger';
      default: return 'secondary';
    }
  };

  return (
    <div className="meeting-room-wrapper">
      {/* Top Bar */}
      <div className="meeting-top-bar bg-dark text-white p-3 d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <h5 className="mb-0 fw-bold">{roomName}</h5>
          {isRecording && (
            <Badge bg="danger" className="d-flex align-items-center gap-2">
              <BsRecordCircle className="animate-pulse" />
              REC {formatTime(recordingTime)}
            </Badge>
          )}
        </div>
        
        <div className="d-flex align-items-center gap-3">
          <Badge bg={getConnectionQualityColor()} className="d-flex align-items-center gap-2">
            {connectionQuality === 'excellent' || connectionQuality === 'good' ? 
              <BsWifi /> : <BsWifiOff />}
            {connectionQuality}
          </Badge>
          
          <Dropdown>
            <Dropdown.Toggle variant="link" className="text-white p-0">
              <BsThreeDots size={20} />
            </Dropdown.Toggle>
            <Dropdown.Menu align="end">
              <Dropdown.Item onClick={() => setShowSelfView(!showSelfView)}>
                <BsCameraVideo className="me-2" /> 
                {showSelfView ? 'Hide Self View' : 'Show Self View'}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setShowSettings(!showSettings)}>
                <BsGear className="me-2" /> Settings
              </Dropdown.Item>
              <Dropdown.Item onClick={toggleRecording}>
                <BsRecordCircle className="me-2" /> 
                {isRecording ? 'Stop Recording' : 'Start Recording'}
              </Dropdown.Item>
              <Dropdown.Item onClick={toggleScreenShare}>
                <BsDisplay className="me-2" /> 
                {isScreenSharing ? 'Stop Sharing' : 'Share Screen'}
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item className="text-danger" onClick={handleLeave}>
                <BsTelephoneX className="me-2" /> Leave Meeting
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {/* Main Content */}
      <div className="meeting-main-content d-flex">
        {/* Video Area */}
        <div className="video-area flex-grow-1 p-3 position-relative">
          {!room ? (
            <div className="h-100 d-flex align-items-center justify-content-center">
              <Card className="text-center p-5 shadow-lg">
                <Card.Body>
                  <div className="mb-4">
                    <BsCameraVideo size={64} className="text-primary" />
                  </div>
                  <h4>Joining Meeting Room</h4>
                  <p className="text-muted mb-4">
                    You're joining as: <strong>{identity}</strong>
                  </p>
                  {isConnecting ? (
                    <div className="d-flex justify-content-center align-items-center">
                      <div className="spinner-border text-primary me-3" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <span>Connecting to meeting...</span>
                    </div>
                  ) : (
                    <p className="text-success">
                      <i className="bi bi-check-circle-fill me-2"></i>
                      Ready to connect
                    </p>
                  )}
                </Card.Body>
              </Card>
            </div>
          ) : (
            <div className="video-grid-container h-100">
              <Row className="h-100 g-3">
                {/* LOCAL VIDEO - YOUR CAMERA */}
                {showSelfView && (
                  <Col xs={12} md={participants.length > 0 ? 6 : 12} lg={participants.length > 1 ? 4 : participants.length > 0 ? 6 : 12}>
                    <div 
                      className="video-tile local-video-tile position-relative h-100"
                      style={{
                        minHeight: '300px',
                        backgroundColor: '#1a1a1a',
                        borderRadius: '12px',
                        overflow: 'hidden'
                      }}
                    >
                      {/* Video Container */}
                      <div 
                        ref={localVideoContainerRef}
                        className="w-100 h-100"
                        style={{ position: 'relative' }}
                      >
                        {/* Video will be attached here */}
                      </div>
                      
                      {/* Overlay with name and status */}
                      <div 
                        className="video-overlay position-absolute"
                        style={{
                          bottom: '12px',
                          left: '12px',
                          display: 'flex',
                          gap: '8px',
                          alignItems: 'center',
                          zIndex: 10
                        }}
                      >
                        <Badge bg="primary">You (Me)</Badge>
                        {isMuted && <Badge bg="danger"><BsMicMute /></Badge>}
                        {isVideoOff && <Badge bg="danger"><BsCameraVideoOff /></Badge>}
                      </div>
                    </div>
                  </Col>
                )}
                
                {/* REMOTE VIDEOS - OTHER PARTICIPANTS */}
                <Col xs={12} md={showSelfView && participants.length > 0 ? 6 : 12} lg={showSelfView && participants.length > 1 ? 8 : showSelfView && participants.length > 0 ? 6 : 12}>
                  <div 
                    ref={remoteMediaDivRef} 
                    className="remote-videos-container h-100"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: participants.length > 1 ? 'repeat(auto-fit, minmax(300px, 1fr))' : '1fr',
                      gap: '16px',
                      alignContent: 'start'
                    }}
                  >
                    {/* Remote videos and audio will be dynamically added here */}
                    {participants.length === 0 && (
                      <div className="text-center text-muted p-5">
                        <BsPeople size={48} />
                        <p className="mt-3">Waiting for others to join...</p>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </div>

        {/* Side Panels */}
        <div className="meeting-side-panels d-flex">
          {/* Participants Panel */}
          {showParticipants && (
            <div className="participants-panel bg-white border-start p-3" style={{ width: '300px' }}>
              <h6 className="fw-bold mb-3">
                <BsPeople className="me-2" />
                Participants ({participants.length + 1})
              </h6>
              <div className="participant-list">
                <div className="participant-item d-flex align-items-center mb-3 p-2 bg-light rounded">
                  <div className="participant-avatar bg-primary text-white rounded-circle me-3 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                    {identity.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-grow-1">
                    <div className="fw-semibold">You</div>
                    <small className="text-muted">Host</small>
                  </div>
                </div>
                
                {participants.map(p => (
                  <div key={p.sid} className="participant-item d-flex align-items-center mb-3 p-2 bg-light rounded">
                    <div className="participant-avatar bg-secondary text-white rounded-circle me-3 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                      {p.identity.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-grow-1">
                      <div className="fw-semibold">{p.identity}</div>
                      <small className="text-muted">Connected</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Chat Panel */}
          {showChat && (
            <div className="chat-panel bg-white border-start p-3" style={{ width: '300px' }}>
              <h6 className="fw-bold mb-3">
                <BsChatDots className="me-2" />
                Meeting Chat
              </h6>
              <div className="chat-messages" style={{ height: 'calc(100% - 120px)', overflowY: 'auto' }}>
                {/* Chat messages will go here */}
              </div>
              <div className="chat-input mt-3">
                <input type="text" className="form-control" placeholder="Type a message..." />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Controls */}
      <div className="meeting-controls bg-dark text-white p-3">
        <div className="d-flex justify-content-center align-items-center gap-3">
          <Button 
            variant={isMuted ? "danger" : "secondary"} 
            onClick={toggleMute}
            className="rounded-circle p-3"
            size="lg"
          >
            {isMuted ? <BsMicMute size={20} /> : <BsMic size={20} />}
          </Button>
          
          <Button 
            variant={isVideoOff ? "danger" : "secondary"} 
            onClick={toggleVideo}
            className="rounded-circle p-3"
            size="lg"
          >
            {isVideoOff ? <BsCameraVideoOff size={20} /> : <BsCameraVideo size={20} />}
          </Button>
          
          <Button 
            variant={isScreenSharing ? "primary" : "secondary"} 
            onClick={toggleScreenShare}
            className="rounded-circle p-3"
            size="lg"
          >
            {isScreenSharing ? <BsDisplayFill size={20} /> : <BsDisplay size={20} />}
          </Button>
          
          <Button 
            variant="danger" 
            onClick={handleLeave}
            className="rounded-pill px-4"
            size="lg"
          >
            <BsTelephoneX className="me-2" />
            Leave
          </Button>
        </div>
        
        <div className="d-flex justify-content-end gap-2 mt-2">
          <Button 
            variant="link" 
            className="text-white p-2"
            onClick={() => setShowParticipants(!showParticipants)}
          >
            <BsPeople size={20} />
          </Button>
          <Button 
            variant="link" 
            className="text-white p-2"
            onClick={() => setShowChat(!showChat)}
          >
            <BsChatDots size={20} />
          </Button>
          <Button 
            variant="link" 
            className="text-white p-2"
          >
            <BsFullscreen size={20} />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default MeetingRoom;